import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAppConfig, useUsers } from '@/hooks/useShared';
import { createGame, updateGameSetup, deleteGame } from '@/hooks/useGameSetup';
import { Button, Card } from '@/components/ui';
import { Game, Location } from '@/lib/types';
import { canManageUsers } from '@/lib/roles';
import { toast } from 'sonner';
import { getCurrentSydneyDate, getCurrentSydneyTime } from '@/lib/date-utils';

interface GameSetupFormProps {
  mode: 'create' | 'edit';
  initialData?: Game;
}

export default function GameSetupForm({ mode, initialData }: GameSetupFormProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config, loading: configLoading } = useAppConfig();
  const { users } = useUsers();

  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [locationId, setLocationId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!config) return;

    if (mode === 'create') {
      setDate(getCurrentSydneyDate());
      setTime(getCurrentSydneyTime());
      setLocationId(config.default_location_id || '');
    } else if (mode === 'edit' && initialData) {
      setDate(initialData.date);
      setTime(initialData.time);
      
      const venue = config.location_list?.find(l => l.label === initialData.venue);
      if (venue) {
        setLocationId(venue.id);
      } else {
        setLocationId('');
      }
    }
  }, [config, mode, initialData]);

  const handleSubmit = async () => {
    console.log('handleSubmit start', { mode, initialData, date, time, locationId });
    setError(null);

    if (!user) {
      setError('You must be logged in to perform this action.');
      return;
    }

    if (!date || !time || !locationId) {
      setError('Please fill in all fields.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (mode === 'create') {
        const venue = config?.location_list?.find(l => l.id === locationId);
        if (!venue) throw new Error('Selected venue not found.');

        const invitedUserIds = users.filter(u => u.auto_add_to_game === true).map(u => u.id);
        console.log('Final Invited User IDs:', invitedUserIds);

        await createGame(
          venue.label,
          date,
          time,
          venue.address || '',
          user.id,
          invitedUserIds
        );
        toast.success('Game scheduled successfully!');
      } else {
        if (!initialData) throw new Error('No game data provided for edit.');

        const venue = config?.location_list?.find(l => l.id === locationId);
        const venueLabel = venue ? venue.label : initialData.venue;
        const venueAddress = venue ? venue.address : initialData.venue_address;

        try {
          await updateGameSetup(initialData.id, {
            venue: venueLabel,
            date,
            time,
            venue_address: venueAddress,
          });
          toast.success('Game updated successfully!');
        } catch (updateErr) {
          console.error('updateGameSetup failed:', updateErr);
          throw updateErr;
        }
      }
      navigate('/setup');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    console.log('handleDelete start', { user, initialData });
    if (!user || !initialData) {
      console.warn('handleDelete: user or initialData is missing');
      return;
    }
    console.log('handleDelete: proceeding to delete');

    setIsSubmitting(true);
    try {
      await deleteGame(initialData.id, user.id);
      toast.success('Game deleted successfully.');
      navigate('/setup');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Access Guard: Admins only
  const hasPermission = user ? canManageUsers(user) : false;

  if (!user || !hasPermission) {
    return (
      <div className="p-4 text-center text-red-500 font-medium">
        You do not have permission to access this form.
      </div>
    );
  }

  if (configLoading) {
    return <div className="text-center text-gray-500">Loading configuration...</div>;
  }

  return (
    <div className="max-w-md mx-auto">
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm font-medium border border-red-100">
          {error}
        </div>
      )}

      <Card className="p-8 space-y-6 rounded-2xl border-none shadow-botanical">
        <div>
          <label className="block text-[10px] font-bold text-aura-text/40 uppercase tracking-wider mb-2">
            Date
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full p-3 rounded-xl border-aura-text/10 bg-aura-background/50 text-slate-900 focus:ring-2 focus:ring-aura-primary focus:bg-white outline-none transition-all"
          />
        </div>

        <div>
          <label className="block text-[10px] font-bold text-aura-text/40 uppercase tracking-wider mb-2">
            Time
          </label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full p-3 rounded-xl border-aura-text/10 bg-aura-background/50 text-slate-900 focus:ring-2 focus:ring-aura-primary focus:bg-white outline-none transition-all"
          />
        </div>

        <div>
          <label className="block text-[10px] font-bold text-aura-text/40 uppercase tracking-wider mb-2">
            Location
          </label>
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            className="w-full p-3 rounded-xl border-aura-text/10 bg-aura-background/50 text-slate-900 focus:ring-2 focus:ring-aura-primary focus:bg-white outline-none transition-all appearance-none"
          >
            <option value="" disabled>Select a venue...</option>
            {config?.location_list?.map((loc: Location) => (
              <option key={loc.id} value={loc.id}>
                {loc.label}
              </option>
            ))}
          </select>
        </div>

        <Button 
          onClick={handleSubmit} 
          disabled={isSubmitting}
          className="w-full py-4 rounded-full font-bold text-lg text-white bg-gradient-to-r from-[#006855] to-[#005b4a] shadow-xl shadow-emerald-500/20 transition-all hover:shadow-emerald-500/30 active:scale-[0.98]"
        >
          {isSubmitting ? 'Processing...' : mode === 'create' ? 'Create Game' : 'Update Game'}
        </Button>

        <div className="pt-8 mt-8 border-t border-slate-100 text-center">
          <span className="text-[10px] font-black uppercase tracking-widest text-red-500 mb-4 block">
            Danger Zone
          </span>
          <Button 
            onClick={handleDelete}
            disabled={isSubmitting}
            variant="ghost"
            className="w-full py-3 rounded-xl font-bold border-error/20 text-error bg-white hover:bg-red-50 transition-colors"
          >
            Delete Session
          </Button>
        </div>

      </Card>
    </div>
  );
}
